import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../api';


function FichajeForm({ token, onFichajeRealizado }) {
  const [tipo, setTipo] = useState('entrada');
  const [faseLoading, setFaseLoading] = useState(null); // null | 'ubicacion' | 'registrando'
  const [resultado, setResultado] = useState(null); // null | { tipo: 'exito'|'error', msg, tipoFichaje }
  const [alertaSinUbicacion, setAlertaSinUbicacion] = useState(null); // ubicacion con error, esperando confirmación

  // Auto-select tipo based on last fichaje
  React.useEffect(() => {
    async function fetchUltimoFichaje() {
      try {
        const apiUrl = `${API_BASE}/api/fichaje/informe`;
        const res = await axios.get(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
        const datos = res.data;
        if (datos && datos.length > 0) {
          const ultimo = datos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
          setTipo(ultimo.tipo === 'entrada' ? 'salida' : 'entrada');
        } else {
          setTipo('entrada');
        }
      } catch {
        setTipo('entrada');
      }
    }
    fetchUltimoFichaje();
  }, [token]);

  const playSound = (type) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (type === 'success') {
        [[523.25, 0], [659.25, 0.14], [783.99, 0.28], [1046.5, 0.42]].forEach(([freq, delay]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = freq; osc.type = 'sine';
          const t = ctx.currentTime + delay;
          gain.gain.setValueAtTime(0, t);
          gain.gain.linearRampToValueAtTime(0.28, t + 0.04);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
          osc.start(t); osc.stop(t + 0.55);
        });
      } else {
        [[440, 0], [370, 0.13], [311, 0.26]].forEach(([freq, delay]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = freq; osc.type = 'sawtooth';
          const t = ctx.currentTime + delay;
          gain.gain.setValueAtTime(0, t);
          gain.gain.linearRampToValueAtTime(0.18, t + 0.03);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
          osc.start(t); osc.stop(t + 0.45);
        });
      }
    } catch (_) {}
  };

  const obtenerUbicacion = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ error: 'Geolocalización no soportada por el navegador' });
        return;
      }
      setFaseLoading('ubicacion');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitud: position.coords.latitude,
            longitud: position.coords.longitude,
            precision: position.coords.accuracy,
            timestamp: new Date(position.timestamp)
          });
        },
        (error) => {
          let errorMsg = 'Error desconocido';
          switch(error.code) {
            case error.PERMISSION_DENIED: errorMsg = 'Permiso de ubicación denegado'; break;
            case error.POSITION_UNAVAILABLE: errorMsg = 'Ubicación no disponible'; break;
            case error.TIMEOUT: errorMsg = 'Timeout obteniendo ubicación'; break;
            default: errorMsg = error.message;
          }
          resolve({ error: errorMsg });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleFichaje = async (e) => {
    e.preventDefault();
    setResultado(null);

    const ubicacion = await obtenerUbicacion();

    // Si no hay ubicación, mostrar advertencia y esperar confirmación del usuario
    if (ubicacion.error) {
      setFaseLoading(null);
      setAlertaSinUbicacion(ubicacion);
      return;
    }

    await _registrarFichaje(ubicacion);
  };

  const _registrarFichaje = async (ubicacion) => {
    setAlertaSinUbicacion(null);
    setFaseLoading('registrando');

    try {
      const apiUrl = `${API_BASE}/api/fichaje`;
      await axios.post(apiUrl, { tipo, ubicacion }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFaseLoading(null);
      playSound('success');

      let detalle = '';
      if (ubicacion.error) detalle = `Sin GPS: ${ubicacion.error}`;
      else if (ubicacion.precision > 100) detalle = `Precisión baja ±${Math.round(ubicacion.precision)}m`;
      else detalle = `±${Math.round(ubicacion.precision)}m de precisión`;

      setResultado({ tipo: 'exito', tipoFichaje: tipo, detalle });
      setTipo(tipo === 'entrada' ? 'salida' : 'entrada');
      if (onFichajeRealizado) onFichajeRealizado();
      setTimeout(() => setResultado(null), 3500);
    } catch (err) {
      setFaseLoading(null);
      playSound('error');
      setResultado({ tipo: 'error', detalle: 'No se pudo registrar el fichaje. Inténtalo de nuevo.' });
      setTimeout(() => setResultado(null), 3500);
      console.error('Error en fichaje:', err);
    }
  };

  return (
    <div className="component-box" style={{
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
      border: '2px solid rgba(102, 126, 234, 0.15)',
      borderRadius: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        
        @keyframes scaleIn {
          0% { transform: scale(0) rotate(-180deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        /* ── Overlay ── */
        @keyframes ffOverlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ffCardIn {
          0%   { opacity: 0; transform: scale(0.7) translateY(30px); }
          60%  { opacity: 1; transform: scale(1.06) translateY(-6px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes ffCheckIn {
          0%   { stroke-dashoffset: 100; opacity: 0; }
          40%  { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes ffRing {
          0%   { transform: scale(0.6); opacity: 0.8; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes ffShake {
          0%,100% { transform: translateX(0); }
          15%     { transform: translateX(-10px) rotate(-4deg); }
          30%     { transform: translateX(10px) rotate(4deg); }
          45%     { transform: translateX(-8px) rotate(-3deg); }
          60%     { transform: translateX(8px) rotate(3deg); }
          75%     { transform: translateX(-4px); }
          90%     { transform: translateX(4px); }
        }
        @keyframes ffParticle {
          0%   { opacity: 1; transform: translate(0,0) scale(1); }
          100% { opacity: 0; transform: var(--to) scale(0.3); }
        }
        @keyframes ffCountdown {
          from { width: 100%; }
          to   { width: 0%; }
        }
        @keyframes ffXIn {
          0%   { opacity: 0; transform: scale(0.3) rotate(90deg); }
          70%  { transform: scale(1.15) rotate(-5deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes ffErrorPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
          50%     { box-shadow: 0 0 0 20px rgba(239,68,68,0); }
        }

        .fichaje-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23667eea' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
          padding-right: 50px;
          transition: all 0.3s;
          background-color: white;
        }
        
        .fichaje-select:hover {
          border-color: #667eea;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
          transform: translateY(-2px);
        }
        
        .fichaje-btn {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          font-size: 1.2rem;
          font-weight: 800;
          padding: 20px 28px;
          border: 4px solid transparent;
          background: 
            linear-gradient(white, white) padding-box,
            linear-gradient(135deg, #FF3B8F 0%, #A855F7 20%, #3B82F6 40%, #06B6D4 60%, #10B981 80%, #FB923C 100%) border-box;
          background-size: auto, 300% 300%;
          color: #1e293b;
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.1),
            0 4px 12px rgba(0, 0, 0, 0.06);
          border-radius: 16px;
          overflow: visible;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          animation: fichajeRainbow 8s linear infinite;
        }
        
        @keyframes fichajeRainbow {
          0% { background-position: 0% 50%, 0% 50%; }
          50% { background-position: 0% 50%, 100% 50%; }
          100% { background-position: 0% 50%, 0% 50%; }
        }
        
        @keyframes siriOrb {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.7; }
          50% { transform: translate(25px, -15px) scale(1.3); opacity: 0.9; }
        }
        
        .fichaje-btn::before {
          content: '';
          position: absolute;
          width: 100px; height: 100px;
          background: radial-gradient(circle, rgba(255, 59, 143, 0.2) 0%, rgba(168, 85, 247, 0.15) 40%, transparent 70%);
          border-radius: 50%;
          top: 50%; left: -20px; transform: translateY(-50%);
          animation: siriOrb 7s ease-in-out infinite;
          filter: blur(30px); pointer-events: none; z-index: -1;
        }
        
        .fichaje-btn::after {
          content: '';
          position: absolute;
          width: 90px; height: 90px;
          background: radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, rgba(16, 185, 129, 0.15) 40%, transparent 70%);
          border-radius: 50%;
          top: 50%; right: -20px; transform: translateY(-50%);
          animation: siriOrb 9s ease-in-out infinite reverse;
          filter: blur(35px); pointer-events: none; z-index: -1;
        }
        
        .fichaje-btn:hover {
          box-shadow: 
            0 12px 40px rgba(0, 0, 0, 0.12),
            0 6px 20px rgba(0, 0, 0, 0.08),
            0 0 60px rgba(168, 85, 247, 0.2);
          transform: translateY(-4px) scale(1.02);
          animation: fichajeRainbow 4s linear infinite;
        }
        
        .fichaje-btn:active {
          transform: translateY(-2px) scale(0.98);
        }
        
        .fichaje-btn:disabled {
          background: linear-gradient(#f1f5f9, #f1f5f9) padding-box;
          border: 3px solid #e2e8f0;
          color: #94a3b8;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transform: none; animation: none; cursor: not-allowed;
        }
        
        .fichaje-btn:disabled::before,
        .fichaje-btn:disabled::after { display: none; }
        
        .ff-spinner {
          display: inline-block;
          width: 22px; height: 22px;
          border: 3px solid rgba(102, 126, 234, 0.25);
          border-top-color: #667eea;
          border-radius: 50%;
          animation: ffSpin 0.7s linear infinite;
          flex-shrink: 0;
        }
        @keyframes ffSpin { to { transform: rotate(360deg); } }
        
        .clock-icon {
          animation: float 3s ease-in-out infinite;
          font-size: 2.5rem;
          filter: drop-shadow(0 4px 8px rgba(102, 126, 234, 0.3));
        }
      `}</style>

      {/* ── Overlay de resultado ── */}
      {resultado && (
        <div
          onClick={() => setResultado(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(10,10,30,0.65)',
            backdropFilter: 'blur(10px)',
            animation: 'ffOverlayIn 0.25s ease-out',
            cursor: 'pointer'
          }}
        >
          {resultado.tipo === 'exito' ? (
            /* ── ÉXITO ── */
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)',
                borderRadius: '28px',
                padding: '48px 44px 40px',
                textAlign: 'center',
                minWidth: '300px',
                maxWidth: '380px',
                animation: 'ffCardIn 0.5s cubic-bezier(0.34,1.56,0.64,1)',
                boxShadow: '0 30px 80px rgba(16,185,129,0.35), 0 10px 30px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                cursor: 'default'
              }}
            >
              {/* Anillos expansivos */}
              {[1,2,3].map(i => (
                <div key={i} style={{
                  position: 'absolute', inset: 0,
                  border: '2px solid rgba(52,211,153,0.5)',
                  borderRadius: '28px',
                  animation: `ffRing ${0.8 + i * 0.35}s ease-out ${i * 0.18}s infinite`
                }} />
              ))}
              {/* Partículas */}
              {[
                ['--to','translate(-90px,-80px)','#34d399'],
                ['--to','translate(90px,-80px)','#6ee7b7'],
                ['--to','translate(-100px,0px)','#10b981'],
                ['--to','translate(100px,0px)','#a7f3d0'],
                ['--to','translate(-70px,80px)','#fbbf24'],
                ['--to','translate(70px,80px)','#f59e0b'],
                ['--to','translate(0,-110px)','#34d399'],
                ['--to','translate(0,110px)','#6ee7b7'],
              ].map(([prop, val, color], i) => (
                <div key={i} style={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: color, marginTop: '-5px', marginLeft: '-5px',
                  [prop]: val,
                  animation: `ffParticle 0.9s ease-out ${0.1 + i * 0.06}s both`
                }} />
              ))}
              {/* Icono check SVG animado */}
              <div style={{ position: 'relative', zIndex: 2, marginBottom: '20px' }}>
                <svg width="80" height="80" viewBox="0 0 80 80" style={{ display: 'block', margin: '0 auto' }}>
                  <circle cx="40" cy="40" r="36" fill="rgba(52,211,153,0.2)" stroke="#34d399" strokeWidth="3" />
                  <polyline
                    points="22,40 35,54 58,26"
                    fill="none"
                    stroke="#6ee7b7"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="100"
                    strokeDashoffset="100"
                    style={{ animation: 'ffCheckIn 0.55s ease-out 0.2s forwards' }}
                  />
                </svg>
              </div>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <p style={{ margin: '0 0 6px', fontSize: '1.5rem', fontWeight: 800, color: '#ecfdf5' }}>
                  {resultado.tipoFichaje === 'entrada' ? '¡Entrada registrada!' : '¡Salida registrada!'}
                </p>
                <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: 'rgba(167,243,208,0.85)', lineHeight: 1.5 }}>
                  {resultado.detalle}
                </p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(167,243,208,0.5)' }}>Toca para cerrar</p>
              </div>
              {/* Barra de progreso countdown */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
                background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 28px 28px', overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #34d399, #6ee7b7)',
                  animation: 'ffCountdown 3.5s linear forwards'
                }} />
              </div>
            </div>
          ) : (
            /* ── ERROR ── */
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #450a0a 0%, #7f1d1d 50%, #991b1b 100%)',
                borderRadius: '28px',
                padding: '48px 44px 40px',
                textAlign: 'center',
                minWidth: '300px',
                maxWidth: '380px',
                animation: 'ffCardIn 0.45s cubic-bezier(0.34,1.56,0.64,1)',
                boxShadow: '0 30px 80px rgba(239,68,68,0.35), 0 10px 30px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                cursor: 'default'
              }}
            >
              {/* Icono X animado */}
              <div style={{ position: 'relative', zIndex: 2, marginBottom: '20px' }}>
                <svg width="80" height="80" viewBox="0 0 80 80" style={{
                  display: 'block', margin: '0 auto',
                  animation: 'ffShake 0.55s ease-out 0.15s both, ffErrorPulse 1.5s ease-out 0.4s 2'
                }}>
                  <circle cx="40" cy="40" r="36" fill="rgba(239,68,68,0.2)" stroke="#f87171" strokeWidth="3" />
                  <line x1="25" y1="25" x2="55" y2="55" stroke="#fca5a5" strokeWidth="5" strokeLinecap="round"
                    style={{ animation: 'ffXIn 0.4s ease-out 0.25s both' }} />
                  <line x1="55" y1="25" x2="25" y2="55" stroke="#fca5a5" strokeWidth="5" strokeLinecap="round"
                    style={{ animation: 'ffXIn 0.4s ease-out 0.35s both' }} />
                </svg>
              </div>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <p style={{ margin: '0 0 6px', fontSize: '1.5rem', fontWeight: 800, color: '#fef2f2' }}>
                  Error al fichar
                </p>
                <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: 'rgba(252,165,165,0.85)', lineHeight: 1.5 }}>
                  {resultado.detalle}
                </p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(252,165,165,0.5)' }}>Toca para cerrar</p>
              </div>
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
                background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 28px 28px', overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #f87171, #fca5a5)',
                  animation: 'ffCountdown 3.5s linear forwards'
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modal aviso sin ubicación ── */}
      {alertaSinUbicacion && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(10,10,30,0.7)',
            backdropFilter: 'blur(8px)',
            animation: 'ffOverlayIn 0.2s ease-out'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            borderRadius: 24,
            padding: '36px 32px 28px',
            textAlign: 'center',
            minWidth: 'min(90vw, 340px)',
            maxWidth: 380,
            boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
            animation: 'ffCardIn 0.4s cubic-bezier(0.34,1.56,0.64,1)'
          }}>
            <div style={{ fontSize: '3.5rem', marginBottom: 12 }}>📍</div>
            <p style={{ margin: '0 0 8px', fontSize: '1.3rem', fontWeight: 800, color: '#f8fafc' }}>
              Sin ubicación GPS
            </p>
            <p style={{ margin: '0 0 24px', fontSize: '0.9rem', color: 'rgba(203,213,225,0.85)', lineHeight: 1.6 }}>
              {alertaSinUbicacion.error}.<br/>
              El fichaje se registrará <strong style={{color:'#fbbf24'}}>sin coordenadas</strong>.<br/>
              ¿Deseas continuar de todos modos?
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setAlertaSinUbicacion(null); setFaseLoading(null); }}
                style={{
                  padding: '0.7em 1.6em', borderRadius: 12,
                  border: '2px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#cbd5e1', fontWeight: 700, fontSize: '1rem',
                  cursor: 'pointer', transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.15)'}
                onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.08)'}
              >
                Cancelar
              </button>
              <button
                onClick={() => _registrarFichaje(alertaSinUbicacion)}
                style={{
                  padding: '0.7em 1.6em', borderRadius: 12,
                  border: '2px solid #f59e0b',
                  background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                  color: '#fff', fontWeight: 700, fontSize: '1rem',
                  cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: '0 4px 14px rgba(245,158,11,0.35)'
                }}
                onMouseEnter={e => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.target.style.transform = 'translateY(0)'}
              >
                Fichar sin GPS
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Efecto de brillo animado en el borde superior */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
        background: 'linear-gradient(90deg, transparent, #667eea, #764ba2, transparent)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 3s linear infinite'
      }}></div>
      
      <div style={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '28px'
      }}>
        <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px', color: '#1e293b' }}>
          <span className="clock-icon">⏰</span>
          Registrar Fichaje
        </h2>
      </div>
      
      <form onSubmit={handleFichaje}>
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', marginBottom: '10px', fontWeight: 600,
            color: '#475569', fontSize: '1rem', letterSpacing: '0.3px'
          }}>
            <span style={{ marginRight: '8px', fontSize: '1.1rem' }}>📋</span>
            Tipo de fichaje
          </label>
          <select 
            value={tipo} 
            onChange={e => setTipo(e.target.value)}
            className="fichaje-select"
            style={{ cursor: 'pointer' }}
          >
            <option value="entrada">🟢 Entrada - Inicio de jornada</option>
            <option value="salida">🔴 Salida - Fin de jornada</option>
          </select>
        </div>
        
        <button 
          type="submit" 
          disabled={!!faseLoading}
          className="fichaje-btn"
        >
          {faseLoading ? (
            <>
              <span className="ff-spinner"></span>
              <span style={{ position: 'relative', zIndex: 1 }}>
                {faseLoading === 'ubicacion' ? 'Obteniendo ubicación...' : 'Registrando fichaje...'}
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: '1.5rem' }}>📍</span>
              <span style={{ position: 'relative', zIndex: 1 }}>Fichar {tipo === 'entrada' ? 'Entrada' : 'Salida'}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default FichajeForm;
